
import React, { useState, useEffect } from 'react'
import Cookies from 'js-cookie'
import axios from 'axios'
import { toast } from 'react-toastify'
import Topbar from '../layouts/Topbar'
import Sidebar from '../layouts/Sidebar'
import { FaBullseye, FaSyncAlt, FaExclamationCircle, FaPlus, FaEdit, FaTrash } from 'react-icons/fa'
import { Link } from 'react-router-dom'
import Swal from 'sweetalert2'
import 'react-toastify/dist/ReactToastify.css'
import '../../Employee/screens/KRAs.css'
import API_BASE_URL from '../../../config/api'

const HRKRAs = () => {
  const baseUrl = API_BASE_URL
  const [kras, setKras] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [selectedKRA, setSelectedKRA] = useState(null)
  const [editModalOpen, setEditModalOpen] = useState(false)
  // Year filter state
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear())
  const [availableYears, setAvailableYears] = useState([])

  const fetchKRAs = async (yearParam = selectedYear) => {
    setLoading(true)
    setError('')
    try {
      const token = Cookies.get('token')
      const response = await axios.get(`${baseUrl}/api/key-result-areas/?year=${encodeURIComponent(yearParam)}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })
      setKras(response.data)
    } catch (err) {
      setError('Failed to fetch KRAs.')
      toast.error('Failed to fetch KRAs.')
    } finally {
      setLoading(false)
    }
  }

  // Fetch available years (unfiltered) once, so the dropdown shows all years
  const fetchAvailableYears = async () => {
    try {
      const token = Cookies.get('token')
      const response = await axios.get(`${baseUrl}/api/key-result-areas/`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })
      const years = Array.from(new Set((Array.isArray(response.data) ? response.data : [])
        .map(k => k?.year)
        .filter(y => y !== undefined && y !== null)))
      .sort((a, b) => a - b)
      setAvailableYears(years)
      // If current selectedYear is not in the list but there are years, default to the latest
      if (years.length > 0 && !years.includes(selectedYear)) {
        setSelectedYear(years[years.length - 1])
      }
    } catch (err) {
      // Silently ignore; dropdown will fallback to current/adjacent years
    }
  }

  const handleEdit = (kra) => {
    setSelectedKRA(kra)
    setEditModalOpen(true)
  }

  const handleDelete = async (kra) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: `Do you want to delete "${kra.name}"?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel'
    })

    if (result.isConfirmed) {
      try {
        const token = Cookies.get('token')
        await axios.delete(`${baseUrl}/api/key-result-areas/${kra.id}/`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        })
        toast.success('KRA deleted successfully!')
        fetchKRAs() // Refresh the list
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Failed to delete KRA.')
      }
    }
  }

  const closeEditModal = () => {
    setEditModalOpen(false)
    setSelectedKRA(null)
  }

  useEffect(() => {
    fetchKRAs(selectedYear)
  }, [selectedYear])

  // On first mount, fetch the list of available years
  useEffect(() => {
    fetchAvailableYears()
  }, [])

  return (
    <>
      <Topbar />
      <Sidebar />
      <div className="kras-container">
        <div className="page-header">
          <div className="header-content">
            <div className="header-left">
              <div className="page-title">
                <h1>Key Result Areas</h1>
                <p>View and manage all Key Result Areas</p>
              </div>
            </div>
            <div className="header-right">
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginRight: 12 }}>
                <label style={{ fontSize: 17, color: '#fff' }}>Year</label>
                <select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                  style={{
                    padding: '6px 10px',
                    border: '1px solid #cbd5e1',
                    borderRadius: 6,
                    background: '#fff',
                    color: '#0f172a',
                    fontSize: 14,
                    cursor: 'pointer'
                  }}
                  title="Filter by year"
                >
                  {(availableYears.length ? availableYears : [selectedYear])
                    .sort((a, b) => a - b)
                    .map(y => (
                      <option key={y} value={y}>{y}</option>
                    ))}
                </select>
              </div>
              <Link to="/hod/create-key-result-area" className="add-button">
                <FaPlus />
                <span>Add KRA</span>
              </Link>
              <button className="refresh-button" onClick={fetchKRAs} disabled={loading} title="Refresh">
                <FaSyncAlt className={loading ? 'spin' : ''} />
              </button>
            </div>
          </div>
        </div>

        <div className="kras-content">
          {loading ? (
            <div className="loading-state">
              <FaSyncAlt className="spin" /> Loading KRAs...
            </div>
          ) : error ? (
            <div className="error-state">
              <FaExclamationCircle /> {error}
            </div>
          ) : kras.length === 0 ? (
            <div className="empty-state">
              <FaBullseye /> No KRAs found.
            </div>
          ) : (
            <div className="kras-card-grid">
              {kras
                .filter(kra => (kra.year !== undefined && kra.year !== null ? kra.year === selectedYear : true))
                .map((kra, idx) => (
                <div className="kra-card" key={kra.id || idx}>
                  <div className="card-header">
                    <div className="card-icon">
                      <FaBullseye />
                    </div>
                    <div className="" style={{ display: 'flex', gap: '8px', alignItems: 'center', paddingRight: '25px' }}>
                      <button 
                        className="action-button edit-button" 
                        title="Edit KRA"
                        onClick={() => handleEdit(kra)}
                        style={{ 
                          color: '#2563eb', 
                          background: 'transparent', 
                          border: '1px solid #2563eb', 
                          borderRadius: '50%', 
                          width: '36px', 
                          height: '36px', 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'center', 
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          fontSize: '14px'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.background = '#e0e7ff';
                          e.target.style.transform = 'scale(1.1)';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.background = 'transparent';
                          e.target.style.transform = 'scale(1)';
                        }}
                      >
                        <FaEdit />
                      </button>
                      <button 
                        className="action-button delete-button" 
                        title="Delete KRA"
                        onClick={() => handleDelete(kra)}
                        style={{ 
                          color: '#dc2626', 
                          background: 'transparent', 
                          border: '1px solid #dc2626', 
                          borderRadius: '50%', 
                          width: '36px', 
                          height: '36px', 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'center', 
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          fontSize: '14px'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.background = '#fee2e2';
                          e.target.style.transform = 'scale(1.1)';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.background = 'transparent';
                          e.target.style.transform = 'scale(1)';
                        }}
                      >
                        <FaTrash />
                      </button>
                    </div>
                  </div>
                  <div className="card-content">
                    <div className="card-title">
                      {kra.name} ({kra.percentage || 0}%)
                      <span style={{ marginLeft: '20px', textAlign: 'right', position: 'absolute', border: 'thin solid #ffc', backgroundColor: '#0f2a94', padding: '3px 7px 3px 7px', color: '#fff', borderRadius: '5px' }}>{kra.year}</span>
                    </div>
                    <div className="card-meta">
                      
                      
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Edit KRA Modal */}
      {editModalOpen && selectedKRA && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2>Edit Key Result Area</h2>
              <button onClick={closeEditModal} className="modal-close-btn">&times;</button>
            </div>
            <div className="modal-body">
              <EditKRAModal 
                kra={selectedKRA} 
                onClose={closeEditModal} 
                onSuccess={() => {
                  closeEditModal()
                  fetchKRAs()
                }}
              />
            </div>
          </div>
        </div>
      )}
    </>
  )
}

// Edit KRA Modal Component
const EditKRAModal = ({ kra, onClose, onSuccess }) => {
  const baseUrl = 'https://pms.burrayhr.com'
  const [loading, setLoading] = useState(false)
  const [form, setForm] = useState({
    name: kra.name,
    percentage: kra.percentage.toString()
  })

  const handleChange = (e) => {
    const { name, value } = e.target
    setForm(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    
    try {
      const token = Cookies.get('token')
      await axios.put(`${baseUrl}/api/key-result-areas/${kra.id}/`, {
        name: form.name,
        percentage: parseInt(form.percentage)
      }, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })
      
      toast.success('KRA updated successfully!')
      onSuccess()
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update KRA.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="edit-kra-form">
      <div className="form-group">
        <label>Name <span className="required">*</span></label>
        <input
          type="text"
          name="name"
          value={form.name}
          onChange={handleChange}
          required
          className="form-input"
          placeholder="Enter KRA name"
          disabled={loading}
        />
      </div>
      
      <div className="form-group">
        <label>Percentage <span className="required">*</span></label>
        <input
          type="number"
          name="percentage"
          value={form.percentage}
          onChange={handleChange}
          required
          className="form-input"
          placeholder="Enter percentage (0-100)"
          min="0"
          max="100"
          disabled={loading}
        />
      </div>
      
      <div className="modal-actions">
        <button
          type="button"
          onClick={onClose}
          className="cancel-button"
          disabled={loading}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="submit-button"
          disabled={loading || !form.name || !form.percentage}
        >
          {loading ? (
            <>
              <FaSyncAlt className="spinner" />
              <span>Updating...</span>
            </>
          ) : (
            <>
              <FaEdit />
              <span>Update KRA</span>
            </>
          )}
        </button>
      </div>
    </form>
  )
}

export default HRKRAs
